import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { siteLocationRenderer } from 'app/components/dataviews/views/legend/legendRenderers';
import { ResultsTableV2 } from 'app/components/resultsTable/ResultsTableV2';
import { combineResults } from 'app/util/queryResults';
import $exports from 'app/stores/$exports';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';
import storeLoader from 'app/stores/storeLoader';
import $query from 'app/stores/query/$query';
import { applyFiltersToAggFilterQuery } from 'app/components/filters/simple/simpleFilterUtils';
import { FlexColumn } from 'core/components';
import { Model } from 'core/model';

const extraDimensionColumns = [
  { key: 'city', label: 'Location', renderer: siteLocationRenderer, minWidth: 180, flexBasis: 500 }
];

const BuildResults = (asset) =>
  // create results as a generic Model since that's cheaper to instantiate
  new Model({
    key: asset.id,
    i_device_site_name: asset.get('title'),
    get address() {
      return asset.get('address');
    },
    get city() {
      return asset.get('city');
    },
    get region() {
      return asset.get('region');
    },
    get country() {
      return asset.get('country');
    }
  });

const _SitesTable = ({
  // fields provided by LightweightDataViewWrapper
  loading,
  dataview,
  queryModel,
  results,
  bucket,
  // injected stores
  $sites,
  // required fields
  filters,
  tableQuery,
  fullyLoaded
}) => {
  // initial sort field values. these are only set one time on initial render, the rest of the time these fields are
  // controlled by ResultsTable.
  const [initialSort, setSort] = useState({ loading: true });
  useEffect(() => {
    $exports.getSettings().then(({ sortResultsBy: field, sortResultsDir: direction }) => {
      // ensure that we don't create combined results multiple times due to delayed in resolving $exports.getSettings()
      // this would cause a confusing re-render flash in results table
      setSort({ field, direction, loading: false });
    });
  }, []);

  // note that ResultsTable is responsible for managing sort state - all we do here is maintain a hash for reloading this page
  const handleSort = useCallback(
    ({ field, direction }) => $exports.setHash({ sortResultsBy: field, sortResultsDir: direction }),
    []
  );

  // this is a very expensive operation and can cause weird side effects if repeated. useMemo is very important here!
  const combinedResults = useMemo(() => {
    const { field, direction, loading: loadingSort } = initialSort;
    if (loading || loadingSort || !fullyLoaded || (!$sites.collection.size && !results.size)) {
      return null;
    }
    return combineResults({
      results,
      assets: $sites.collection,
      filters,
      assetMapper: (asset) => asset.get('title'),
      resultMapper: (result) => result.get('i_device_site_name'),
      buildResultFn: BuildResults,
      sort: field,
      sortDir: direction
    });
  }, [initialSort, $sites.collection, results, filters, loading, fullyLoaded]);

  return (
    <FlexColumn height="100%" mt={2}>
      <ResultsTableV2
        showActionsMenu
        query={tableQuery}
        loading={!fullyLoaded || !$sites.collection.hasFetched}
        dataview={dataview}
        queryModel={queryModel}
        queryResultsCollection={combinedResults}
        bucket={bucket}
        dimensionOverrides={extraDimensionColumns}
        onSort={handleSort}
        virtualized
        rowHeight={40}
      />
    </FlexColumn>
  );
};

const SitesTableInternal = storeLoader('$sites')(observer(_SitesTable));

export const SitesTable = observer(({ lookbackSeconds, aggregateType, filters }) => {
  const tableQuery = useMemo(() => {
    const query = $query.get('inboundOutboundInternalAggregateQuery', {
      metric: ['i_device_site_name'],
      lookback_seconds: lookbackSeconds,
      aggregateTypes: [aggregateType],
      topx: 350,
      depth: 350
    });

    applyFiltersToAggFilterQuery(query, filters);
    return query;
  }, [lookbackSeconds, aggregateType, filters]);

  return (
    <LightweightDataViewWrapper query={tableQuery}>
      {(dataProps) => <SitesTableInternal {...dataProps} filters={filters} tableQuery={tableQuery} />}
    </LightweightDataViewWrapper>
  );
});
